import { useRef, useEffect } from "react";
import { StopBlock, OperatorRecord } from "../../types";
import { i18nLanguage } from "i18n";
import { getLocalizedString } from "../../utils/getLocalizedString";
import { getCloudinaryImageUrl } from "../../utils/getCloudinaryImageUrl";
import { stripHtml } from "shared/utils";
import { formatPhoneNumber } from "shared/utils";
import {
  DATE_PATTERN_TIME,
  formatDate,
  getOpenPeriodsTimeOfDay,
} from "shared/utils";
import { toZonedTime } from "date-fns-tz";

export type StopInfoPanelData = {
  stop: StopBlock;
  operator: OperatorRecord;
  markerNumber: number;
};

type StopInfoPanelProps = {
  data: StopInfoPanelData | null;
  language: i18nLanguage;
  onClose: () => void;
  onOutboundClick?: (operatorEntityId: string, linkType: "google_maps" | "website") => void;
};

const formatAddress = (
  address?: {
    line1?: string | null;
    city?: string | null;
    subdivision?: string | null;
  } | null,
) => {
  const parts = [address?.line1, address?.city, address?.subdivision].filter(Boolean);
  return parts.join(", ");
};

const DAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
type DayKey = (typeof DAY_ORDER)[number];

const DAY_LABELS: Record<i18nLanguage, Record<DayKey, string>> = {
  en: {
    mon: "Monday",
    tue: "Tuesday",
    wed: "Wednesday",
    thu: "Thursday",
    fri: "Friday",
    sat: "Saturday",
    sun: "Sunday",
  },
  fr: {
    mon: "Lundi",
    tue: "Mardi",
    wed: "Mercredi",
    thu: "Jeudi",
    fri: "Vendredi",
    sat: "Samedi",
    sun: "Dimanche",
  },
  es: {
    mon: "Lunes",
    tue: "Martes",
    wed: "Miercoles",
    thu: "Jueves",
    fri: "Viernes",
    sat: "Sabado",
    sun: "Domingo",
  },
};

const CLOSED_LABEL: Record<i18nLanguage, string> = {
  en: "Closed",
  fr: "Ferme",
  es: "Cerrado",
};

const HOURS_HEADER_LABEL: Record<i18nLanguage, string> = {
  en: "Hours",
  fr: "Heures",
  es: "Horas",
};

const DAY_HEADER_LABEL: Record<i18nLanguage, string> = {
  en: "Day",
  fr: "Jour",
  es: "Dia",
};

type HoursRange = { start?: string | null; end?: string | null } | null | undefined;

const formatTime = (time: string) =>
  formatDate(toZonedTime(time, "UTC"), DATE_PATTERN_TIME);

/** One line per open period, so a day with a break reads as two lines. */
const formatHoursRange = (
  range: HoursRange,
  breaks: HoursRange[] | null | undefined,
  closedLabel: string,
): string[] => {
  const periods = getOpenPeriodsTimeOfDay(range, breaks);
  if (periods.length === 0) return [closedLabel];
  return periods.map(period => `${formatTime(period.start)} - ${formatTime(period.end)}`);
};

const BackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="14" y1="8" x2="2" y2="8" />
    <polyline points="8 2 2 8 8 14" />
  </svg>
);

export const StopInfoPanel = ({ data, language, onClose, onOutboundClick }: StopInfoPanelProps) => {
  const isOpen = data !== null;
  const panelRef = useRef<HTMLDivElement>(null);

  // Scroll panel back to top when opening
  useEffect(() => {
    if (isOpen && panelRef.current) {
      panelRef.current.scrollTop = 0;
    }
  }, [isOpen, data?.stop._id]);

  // Derive display data (safe even when data is null — won't render)
  const operator = data?.operator;
  const stop = data?.stop;
  const markerNumber = data?.markerNumber ?? 0;

  const operatorName = operator?.name
    ? getLocalizedString(operator.name, language)
    : null;

  const rawDescription = operator?.description
    ? getLocalizedString(operator.description, language)
    : "";
  const description = rawDescription ? stripHtml(rawDescription) : "";

  const address = formatAddress(operator?.contact?.address);
  const phone = operator?.contact?.phone ?? null;
  const website = operator?.contact?.links?.website ?? null;
  const hoursOfOperation = operator?.hoursOfOperation ?? null;
  const hasHoursOfOperation = DAY_ORDER.some((day) => day in (hoursOfOperation ?? {}));
  const dayLabels = DAY_LABELS[language] ?? DAY_LABELS.en;
  const closedLabel = CLOSED_LABEL[language] ?? CLOSED_LABEL.en;
  const dayHeaderLabel = DAY_HEADER_LABEL[language] ?? DAY_HEADER_LABEL.en;
  const hoursHeaderLabel = HOURS_HEADER_LABEL[language] ?? HOURS_HEADER_LABEL.en;

  const imageId = stop?.featuredImageId ?? operator?.images?.[0]?._id ?? null;
  const heroImageUrl = getCloudinaryImageUrl(imageId, "f_auto,w_800,c_limit");

  const linkUrl = stop?.link?.url ?? null;
  const linkLabel = stop?.link?.label
    ? getLocalizedString(stop.link.label, language)
    : null;

  const businessType = operator?.type
    ? operator.type
        .replace(/_/g, " ")
        .replace(/business type/i, "")
        .trim()
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : null;

  return (
    <div
      ref={panelRef}
      className="tripper-info-panel"
      style={{
        pointerEvents: isOpen ? "auto" : "none",
        opacity: isOpen ? 1 : 0,
        transform: isOpen ? "scale(1)" : "scale(0.96)",
        filter: isOpen ? "blur(0px)" : "blur(6px)",
      }}
    >
      {/* Hero image */}
      {heroImageUrl ? (
        <div className="tripper-info-panel__hero">
          <img src={heroImageUrl} alt={operatorName ?? ""} />
        </div>
      ) : (
        <div className="tripper-info-panel__hero tripper-info-panel__hero--empty" />
      )}

      {/* Back button */}
      <button
        type="button"
        className="tripper-info-panel__back"
        onClick={onClose}
        aria-label="Back"
      >
        <BackIcon />
        <span>Back</span>
      </button>

      {/* Content */}
      <div className="tripper-info-panel__content">
        {/* Badge row */}
        <div className="tripper-info-panel__badge-row">
          <div className="tripper-info-panel__badges">
            <div className="tripper-badge">{markerNumber}</div>
            {businessType && <span className="tripper-tag">{businessType}</span>}
          </div>
          {website && (
            <a
              href={website}
              target="_blank"
              rel="noopener noreferrer"
              className="tripper-info-panel__website-btn"
              onClick={() => operator?._entityId && onOutboundClick?.(operator._entityId, "website")}
            >
              Visit website
            </a>
          )}
        </div>

        {/* Business details */}
        <div className="tripper-info-panel__details">
          {operatorName && (
            <h2 className="tripper-info-panel__name">{operatorName}</h2>
          )}
          {address && (
            <p className="tripper-info-panel__address">{address}</p>
          )}
          {phone && (
            <div className="tripper-info-panel__contact">
              <p>{formatPhoneNumber(phone)}</p>
            </div>
          )}
        </div>

        {/* Description */}
        {description && (
          <div className="tripper-info-panel__description">
            {description.split("\n\n").map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        )}

        {/* Operating hours */}
        {hasHoursOfOperation && (
          <div className="tripper-info-panel__hours">
            <h3 className="tripper-info-panel__hours-title">Operating hours</h3>
            <div className="tripper-info-panel__hours-grid" role="table" aria-label="Operating hours">
              <div className="tripper-info-panel__hours-row tripper-info-panel__hours-row--header">
                <div className="tripper-info-panel__hours-header tripper-info-panel__hours-header-day">
                  {dayHeaderLabel}
                </div>
                <div className="tripper-info-panel__hours-header tripper-info-panel__hours-header-value">
                  {hoursHeaderLabel}
                </div>
              </div>

              {DAY_ORDER.map((day, index) => (
                <div
                  key={day}
                  className={`tripper-info-panel__hours-row ${index % 2 === 0 ? "tripper-info-panel__hours-row--even" : "tripper-info-panel__hours-row--odd"}`}
                >
                  <div className="tripper-info-panel__hours-day">{dayLabels[day]}</div>
                  <div className="tripper-info-panel__hours-value">
                    {formatHoursRange(
                      hoursOfOperation?.[day],
                      hoursOfOperation?.breaks?.[day],
                      closedLabel,
                    ).map(line => (
                      <div key={line}>{line}</div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Custom link */}
        {linkUrl && (
          <a
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="tripper-stop__link-btn"
          >
            {linkLabel || "Learn more"}
          </a>
        )}
      </div>
    </div>
  );
};
